/**
 * Regras do Painel de Recompra. Sem React, sem MUI — só cálculo, para dar para
 * testar sem abrir o navegador (é o que `verifica.ts` faz).
 *
 * Porte fiel das regras do painel publicado (`gen_painel_recompra.py` do projeto
 * fiesta_party_dados, versão de 15/09/2026). As decisões de negócio embutidas aqui
 * estão todas documentadas no `RUNBOOK.md` daquele projeto — nenhuma foi inventada
 * nesta tradução.
 */

import type {
  Descontinuado,
  ItemPedido,
  LinhaRecompra,
  ParadoRecompra,
  Urgencia,
} from './tipos'

/**
 * Fornecedor confirmado pela Mariana em 11/09/2026: todo SKU ATIVO sem fornecedor no
 * cadastro do Tiny é da Bellocopo. Não vem do ERP nem de dedução pelo nome do produto —
 * é confirmação humana, e a tela diz isso em toda linha onde a regra se aplica.
 * ⚠️ `BELLO COMERCIO E DISTRIBUICAO DE DESCARTAVEIS LTDA` é OUTRO fornecedor.
 */
export const FORNECEDOR_CONFIRMADO = 'BELLOCOPO DESCARTAVEIS LTDA'

export const SEM_FORNECEDOR = '— sem fornecedor —'

export const ROTULO_URGENCIA: Record<Urgencia, string> = {
  comprar_agora: 'Comprar agora',
  atencao: 'Atenção',
  ok: 'OK',
  sem_giro: 'Sem giro',
}

/** Mesmo id usado no painel do Artifact — assim a marcação antiga pode ser migrada. */
export const sid = (s: string) =>
  (s || '').replace(/[^A-Za-z0-9_\-.~:@+]/g, '_').slice(0, 180) || '_'
export const idProduto = (codigo: string) => `p_${sid(codigo)}`
export const idFornecedor = (nome: string) => `f_${sid(nome)}`

export type MapaDescontinuados = Record<string, Descontinuado>

export function mapaDescontinuados(itens: Descontinuado[] | undefined): MapaDescontinuados {
  const m: MapaDescontinuados = {}
  for (const d of itens || []) m[d.id] = d
  return m
}

/**
 * Cadastro incompleto: sem saldo E sem preço de custo ao mesmo tempo. É fato objetivo
 * dos dois campos, não suposição sobre o produto — pode ser ruptura real ou cadastro
 * pela metade, e o painel não tem como distinguir. Só sinaliza.
 */
export const cadastroIncompleto = (l: LinhaRecompra) => l.estoque === 0 && !l.custo_unitario

export const produtoDescontinuado = (l: LinhaRecompra, desc: MapaDescontinuados) =>
  !!desc[idProduto(l.codigo)]

export const fornecedorDescontinuado = (f: string | null, desc: MapaDescontinuados) =>
  !!(f && desc[idFornecedor(f)])

/** Fora de linha por decisão dela: pelo próprio SKU, pelo fornecedor dele, ou —
 *  quando não tem fornecedor no cadastro — pela Bellocopo, que é quem o cobre. */
export const descontinuado = (l: LinhaRecompra, desc: MapaDescontinuados) =>
  produtoDescontinuado(l, desc) ||
  fornecedorDescontinuado(l.fornecedor, desc) ||
  (!l.fornecedor && fornecedorDescontinuado(FORNECEDOR_CONFIRMADO, desc))

export const ativos = (linhas: LinhaRecompra[], desc: MapaDescontinuados) =>
  linhas.filter((l) => !descontinuado(l, desc))

/** O fornecedor do cadastro, ou o confirmado quando o SKU está ativo e sem um.
 *  SKU descontinuado sem fornecedor continua sem — a regra não vale para ele. */
export const fornecedorDe = (l: LinhaRecompra, desc: MapaDescontinuados): string | null =>
  l.fornecedor || (descontinuado(l, desc) ? null : FORNECEDOR_CONFIRMADO)

/** true quando o fornecedor exibido veio da confirmação dela, não do ERP. */
export const fornecedorEhConfirmado = (l: LinhaRecompra, desc: MapaDescontinuados) =>
  !l.fornecedor && !descontinuado(l, desc)

export const somaValor = (arr: Array<{ valor_estoque: number | null }>) =>
  arr.reduce((s, x) => s + (x.valor_estoque || 0), 0)

export const somaUnidades = (arr: Array<{ estoque: number }>) =>
  arr.reduce((s, x) => s + (x.estoque || 0), 0)

export type ValorEstoque = {
  /** com giro, CMV confiável, ainda comprado */
  ativos: LinhaRecompra[]
  /** com giro e CMV, mas marcado como fora de linha — estoque a escoar */
  descontinuados: LinhaRecompra[]
  /** CMV acima de 3x o preço de saída na própria NF: fora do total, nunca apagado */
  emRevisao: LinhaRecompra[]
  /** tem saldo e nenhum CMV na base: "sem CMV", nunca zero */
  semCmv: LinhaRecompra[]
  /** sem venda no período: sai em unidades, SEM valor (decisão de 15/09/2026) */
  parados: ParadoRecompra[]
  total: number
}

/**
 * Valor imobilizado = saldo do Tiny × CMV de `ecommerce_financial.cmvs`.
 *
 * Três regras que não são óbvias e não devem ser "simplificadas":
 *  1. o custo é SÓ o CMV. `preco_custo` do Tiny não entra (cobre menos SKUs e traz
 *     R$ 1,00 de marcador na linha importada). Sem CMV = "sem CMV", nunca zero.
 *  2. CMV acima de 3× o preço médio de saída na própria nota vai para "custo em
 *     revisão", fora do total — e nunca é apagado.
 *  3. SÓ SE VALORA O QUE TEM GIRO. Sem nota no período não há preço de saída para
 *     conferir o custo contra, e era ali que estavam R$ 602 mil de CMV corrompido
 *     (LS0044: copo de R$ 8,15 com CMV de R$ 3.386, R$ 328 mil sozinho).
 */
export function valorEstoque(
  linhas: LinhaRecompra[],
  parados: ParadoRecompra[],
  desc: MapaDescontinuados,
): ValorEstoque {
  const comSaldo = linhas.filter((l) => l.estoque > 0)
  const vivos = comSaldo.filter((l) => l.valor_estoque && !l.cmv_suspeito && !descontinuado(l, desc))
  const mortos = comSaldo.filter((l) => l.valor_estoque && !l.cmv_suspeito && descontinuado(l, desc))
  const emRevisao = comSaldo.filter((l) => l.valor_estoque && l.cmv_suspeito)
  const semCmv = comSaldo.filter((l) => !l.valor_estoque)
  return {
    ativos: vivos,
    descontinuados: mortos,
    emRevisao,
    semCmv,
    parados,
    total: somaValor(vivos) + somaValor(mortos),
  }
}

// --------------------------------------------------------------------- filtro e ordem

export type Filtros = {
  /** busca por SKU ou nome */
  q: string
  /** urgência, ou '__inc__' para o filtro de cadastro incompleto */
  u: string
  /** fornecedor, ou '__sem__' para os sem fornecedor nenhum */
  f: string
  /** 'nacional' | 'importado' | '' */
  cl: string
}

export const FILTROS_VAZIOS: Filtros = { q: '', u: '', f: '', cl: '' }

export function filtrar(
  linhas: LinhaRecompra[],
  filtros: Filtros,
  desc: MapaDescontinuados,
): LinhaRecompra[] {
  const t = filtros.q.trim().toLowerCase()
  return ativos(linhas, desc).filter((l) => {
    if (t && !`${l.codigo} ${l.nome || ''}`.toLowerCase().includes(t)) return false
    if (filtros.u) {
      if (filtros.u === '__inc__') {
        if (!cadastroIncompleto(l)) return false
      } else if (l.urgencia !== filtros.u) return false
    }
    if (filtros.cl && l.classificacao !== filtros.cl) return false
    if (filtros.f) {
      const f = fornecedorDe(l, desc)
      if (filtros.f === '__sem__' ? !!f : f !== filtros.f) return false
    }
    return true
  })
}

export type ChaveOrdem =
  | 'nome'
  | 'fornecedor'
  | 'urgencia'
  | 'estoque'
  | 'valor_estoque'
  | 'giro_diario'
  | 'cobertura_dias'
  | 'ponto_reposicao'
  | 'quantidade_sugerida'
  | 'unidades_periodo'
  | 'lead_time_dias'

export type Ordem = { k: ChaveOrdem; dir: 1 | -1 }

/** Cobertura sobe (o mais crítico primeiro); as outras colunas descem. */
export const ORDEM_PADRAO: Ordem = { k: 'cobertura_dias', dir: 1 }

export function proximaOrdem(atual: Ordem, k: ChaveOrdem): Ordem {
  if (atual.k === k) return { k, dir: (atual.dir === 1 ? -1 : 1) as 1 | -1 }
  return { k, dir: k === 'cobertura_dias' ? 1 : -1 }
}

export function ordenar(
  arr: LinhaRecompra[],
  ord: Ordem,
  desc: MapaDescontinuados,
): LinhaRecompra[] {
  const valor = (l: LinhaRecompra): string | number => {
    if (ord.k === 'fornecedor') return fornecedorDe(l, desc) || ''
    const x = l[ord.k]
    // nulo sempre no fim, qualquer que seja a direção
    if (x == null) return ord.dir > 0 ? 1e15 : -1e15
    return x as string | number
  }
  return [...arr].sort((a, b) => {
    const va = valor(a)
    const vb = valor(b)
    if (typeof va === 'string' || typeof vb === 'string') {
      return String(va).localeCompare(String(vb)) * ord.dir
    }
    return (va - vb) * ord.dir
  })
}

/** O total acompanha o filtro: escolher um fornecedor mostra o estoque dele. */
export function totaisDaLista(arr: LinhaRecompra[]) {
  const bons = arr.filter((l) => l.valor_estoque && !l.cmv_suspeito)
  const revisao = arr.filter((l) => l.valor_estoque && l.cmv_suspeito)
  const semCmv = arr.filter((l) => l.estoque > 0 && !l.valor_estoque)
  return {
    valor: somaValor(bons),
    unidades: somaUnidades(bons),
    revisaoValor: somaValor(revisao),
    revisaoSkus: revisao.length,
    semCmvSkus: semCmv.length,
  }
}

export type GrupoFornecedor = {
  nome: string
  itens: LinhaRecompra[]
  urgentes: number
  valorEstoque: number
  /** true quando o nome é um fornecedor de verdade (dá para descontinuar) */
  real: boolean
}

/**
 * Por fornecedor: só o que precisa de compra (comprar_agora e atenção). Cada bloco
 * é um pedido em potencial, e vem ordenado por quem tem mais urgência.
 */
export function agruparPorFornecedor(
  linhas: LinhaRecompra[],
  desc: MapaDescontinuados,
): GrupoFornecedor[] {
  const g: Record<string, LinhaRecompra[]> = {}
  for (const l of ativos(linhas, desc)) {
    if (l.urgencia === 'ok' || l.urgencia === 'sem_giro') continue
    const nome = fornecedorDe(l, desc) || SEM_FORNECEDOR
    ;(g[nome] ||= []).push(l)
  }
  return Object.entries(g)
    .map(([nome, itens]) => ({
      nome,
      itens: [...itens].sort(
        (a, b) => (a.cobertura_dias ?? 9e9) - (b.cobertura_dias ?? 9e9),
      ),
      urgentes: itens.filter((x) => x.urgencia === 'comprar_agora').length,
      valorEstoque: somaValor(itens.filter((x) => x.valor_estoque && !x.cmv_suspeito)),
      real: nome !== SEM_FORNECEDOR,
    }))
    .sort((a, b) => b.urgentes - a.urgentes)
}

export function listaFornecedores(linhas: LinhaRecompra[]): string[] {
  const s = new Set<string>()
  for (const l of linhas) if (l.fornecedor) s.add(l.fornecedor)
  // a Bellocopo entra mesmo que nenhum SKU a traga do cadastro: é para onde vão
  // os SKUs ativos sem fornecedor
  s.add(FORNECEDOR_CONFIRMADO)
  return [...s].sort((a, b) => a.localeCompare(b))
}

// ------------------------------------------------------------------ pedido em montagem

export type GrupoPedido = { nome: string; itens: ItemPedido[]; unidades: number }

export function agruparPedido(itens: ItemPedido[]): GrupoPedido[] {
  const g: Record<string, ItemPedido[]> = {}
  for (const i of itens) (g[i.forn || SEM_FORNECEDOR] ||= []).push(i)
  return Object.entries(g)
    .map(([nome, its]) => ({
      nome,
      itens: its,
      unidades: its.reduce((s, i) => s + (Number(i.qtd) || 0), 0),
    }))
    .sort((a, b) => b.itens.length - a.itens.length)
}

/**
 * Colunas do arquivo exportado.
 * ⚠️ O layout de importação do Tiny NÃO foi confirmado. Se o ERP exigir um formato
 * específico, é aqui que se troca — e só aqui.
 */
export const COLUNAS_PEDIDO: Array<[string, string]> = [
  ['sku', 'Código'],
  ['nome', 'Descrição'],
  ['forn', 'Fornecedor'],
  ['qtd', 'Quantidade'],
  ['est', 'Estoque atual'],
  ['giro', 'Giro/dia'],
  ['cob', 'Cobertura (dias)'],
  ['sug', 'Sugerido pelo painel'],
]

export type Celula = string | number

export function linhasExport(
  itens: ItemPedido[],
  porSku: Map<string, LinhaRecompra>,
): Celula[][] {
  const aoa: Celula[][] = [COLUNAS_PEDIDO.map((c) => c[1])]
  for (const it of itens) {
    const l = porSku.get(it.sku)
    aoa.push([
      it.sku,
      it.nome || l?.nome || '',
      it.forn || '',
      Number(it.qtd) || 0,
      l?.estoque ?? '',
      l?.giro_diario == null ? '' : Number(l.giro_diario.toFixed(3)),
      l?.cobertura_dias == null ? '' : Number(l.cobertura_dias.toFixed(1)),
      it.sug ?? '',
    ])
  }
  return aoa
}

/**
 * Layout de importação de Pedido de Compra do Tiny — as 13 colunas do modelo que o
 * próprio ERP exporta (`pedidos_compra.xls`, conferido em 18/09/2026). Uma linha por
 * item; os dados do pedido (data, contato, situação) repetem em todas as linhas dele.
 *
 * ⚠️ O que fica VAZIO, e por quê:
 *  - `Valor unitário`: o painel não sabe o preço negociado com o fornecedor — isso é
 *    negociação, não dado da base. Decisão da Mariana em 18/09/2026: sai em branco e
 *    ela preenche antes de importar. Nem `preco_custo` nem CMV entram aqui: o primeiro
 *    falta em 229 SKUs e traz R$ 1,00 de marcador, o segundo é custo de saída, não
 *    preço da próxima compra.
 *  - `ID`, `ID contato`, `ID produto`: id vazio é mais seguro que id falso. O Tiny
 *    casa o produto pelo `Código (SKU)` e o fornecedor pelo `Nome do contato`.
 *  - `Número da ordem de compra`: quem numera é o ERP.
 */
export const COLUNAS_OC_TINY = [
  'ID',
  'Data',
  'ID contato',
  'Nome do contato',
  'Desconto',
  'Observações',
  'Situação',
  'ID produto',
  'Descrição',
  'Quantidade',
  'Valor unitário',
  'Número da ordem de compra',
  'Código (SKU)',
]

/** Situação inicial, exatamente como o modelo do Tiny escreve. */
export const SITUACAO_OC = 'Em aberto'

const dataBr = (d: Date) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`

/** Uma linha por item, no layout que o Tiny importa. */
export function linhasExportTiny(
  itens: ItemPedido[],
  porSku: Map<string, LinhaRecompra>,
  hoje = new Date(),
): Celula[][] {
  const aoa: Celula[][] = [COLUNAS_OC_TINY]
  const data = dataBr(hoje)
  for (const it of itens) {
    const l = porSku.get(it.sku)
    aoa.push([
      '', // ID — o ERP numera
      data,
      '', // ID contato — casa pelo nome
      it.forn || '',
      0, // Desconto
      '', // Observações
      SITUACAO_OC,
      '', // ID produto — casa pelo Código (SKU)
      it.nome || l?.nome || '',
      Number(it.qtd) || 0,
      '', // Valor unitário — preenchido à mão (ver comentário do layout)
      '', // Número da ordem de compra
      it.sku,
    ])
  }
  return aoa
}

/** `;` + BOM: é o que o Excel em português abre sem perguntar nada. */
export function paraCsv(aoa: Celula[][]): string {
  const esc = (v: Celula) => {
    const s = String(v ?? '')
    return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  return `﻿${aoa.map((r) => r.map(esc).join(';')).join('\r\n')}`
}

/** TSV para colar direto numa planilha — funciona sem baixar arquivo nenhum. */
export function paraTsv(aoa: Celula[][]): string {
  return aoa.map((r) => r.map((v) => String(v ?? '')).join('\t')).join('\n')
}

export function nomeArquivo(
  forn: string | null,
  ext: string,
  hoje = new Date(),
  prefixo = 'pedido',
): string {
  const dia = hoje.toISOString().slice(0, 10)
  const alvo = forn ? sid(forn).slice(0, 40).replace(/_+/g, '_') : 'completo'
  return `${prefixo}_${alvo}_${dia}.${ext}`
}

// ------------------------------------------------------------------------- formatação

export const fmtInt = (n: number | null | undefined) =>
  n == null ? '—' : Math.round(n).toLocaleString('pt-BR')

export const fmtDec1 = (n: number | null | undefined) =>
  n == null
    ? '—'
    : n.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

export const fmtBrl = (n: number | null | undefined) =>
  n == null
    ? '—'
    : n.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 0,
      })

export const fmtDataHora = (iso: string | null | undefined) => {
  if (!iso) return 'não informado'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return 'não informado'
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const fmtDia = (iso: string | null | undefined) =>
  !iso ? '—' : iso.slice(0, 10).split('-').reverse().join('/')
